import { useCallback, useEffect } from "react";
import { mountSdk, registerSdkCommands } from "./sdk";
import { useAppDispatch, useAppSelector } from "./store";

import { useTheme } from "./theme";
import "./i18n"; // i18n is a side effect, it sets up the i18n instance.

import { CurrentRoute, RouterProvider, useParams, useRouter } from "./router";
import { Conversation, setConversation } from "./slices/chatSlice";

import ListConversationsPage from "./ListConversationsPage";
import PositionMode from "./PositionMode";
import useSynchronizedPing from "./useSynchronizedPing";
import Chat from "./chat/Chat";
import AccessGate from "./chat/AccessGate";

const PageNotFound = () => (
  <div>
    <h1>Oops! Page not found</h1>
    <p>The page you're looking for doesn't exist.</p>
  </div>
);

function ConfigLauncher() {
  const { status } = useAppSelector((state) => state.config);

  useEffect(() => {
    const sdk = mountSdk();
    registerSdkCommands(sdk);
    sdk.enable();
  }, []);

  if (status == "succeeded") {
    return <Launcher />;
  } else {
    return <></>;
  }
}

function Launcher() {
  const mode = useAppSelector((state) => state.config.data.mode);
  const frameId = useAppSelector((state) => state.config.data.frameId);
  useTheme(frameId);
  // NOTE(memben): this does only handle user errors, not necessarily all API errors
  //               for the future we should unify user errors into API errors and handle them here
  const userError = useAppSelector((state) => state.user.error);
  useEffect(() => {
    const action = userError?.body.sdk_action as undefined | Array<any>;
    if (Array.isArray(action)) window.$botbrains.push(action);
  }, [userError]);

  const onInForeground = useCallback(
    () => window.$botbrains.emit("frame.loaded"),
    []
  );

  useSynchronizedPing({
    onceInForeground: true,
    onInForeground,
  });

  const routes = [
    {
      path: "/",
      component: ResolveApp,
    },
    {
      path: "/frames/:frameId/conversations",
      component: ListConversationsPage,
    },
    {
      path: "/frames/:frameId/conversations/:conversationId",
      component: ConversationPage,
    },
    {
      path: "/frames/:frameId/chat",
      component: Chat,
    },
  ];
  return (
    <RouterProvider routes={routes} initialPath="/">
      <PositionMode mode={mode}>
        <AccessGate>
          <CurrentRoute fallback={PageNotFound} />
        </AccessGate>
      </PositionMode>
    </RouterProvider>
  );
}

function ResolveApp() {
  const { navigate } = useRouter();
  const { data, status } = useAppSelector((state) => state.conversations);
  const { frameId, experimentalListConversations } = useAppSelector(
    (state) => state.config.data
  );

  let target = `/frames/${frameId}/chat`;
  if (experimentalListConversations) {
    const cid = data[0]?.conversation_id;
    if (status === "succeeded") {
      if (data.length > 0) target = `/frames/${frameId}/conversations/${cid}`;
      else target = `/frames/${frameId}/chat`;
    }
  } else {
    const cid = data[0]?.conversation_id;
    if (status === "succeeded")
      target = cid
        ? `/frames/${frameId}/conversations/${cid}`
        : `/frames/${frameId}/chat`;
    else if (status == "failed") target = "/error";
    else target = ""; // block until status chosen.
  }

  useEffect(() => {
    if (target) navigate(target);
  }, [target]);
  return <></>;
}

function ConversationPage() {
  const dispatch = useAppDispatch();
  const { frameId, conversationId } = useParams();
  const { navigate } = useRouter();
  const conversations = useAppSelector((state) => state.conversations);
  const conversation = conversations.data.find(
    (c) => c.conversation_id === conversationId
  );
  useEffect(() => {
    const func = async (conversation: Conversation) => {
      await dispatch(setConversation(conversation));
      navigate(`/frames/${frameId}/chat`);
    };

    if (conversation) {
      func(conversation);
    }
  }, [conversation]);

  return <></>;
}

export default ConfigLauncher;
